
import React, { useEffect, lazy, Suspense, memo } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import Sidebar from '@/components/dashboard/nav/Sidebar';
import MobileNav from '@/components/dashboard/nav/MobileNav';
import LayoutDeploymentTracker from './LayoutDeploymentTracker';
import { useAICopilot } from '@/hooks/useAICopilot';
import { useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { lazyLoad, LazyLoadWrapper } from '@/utils/performance/lazyLoad';

// Lazy load non-critical components
const MemoryUsageIndicator = lazy(() => 
  import('@/components/system/MemoryUsageIndicator')
);

// Lazy load the AICopilot components with proper options
const AICopilot = lazyLoad(
  () => import('@/components/ai-copilot/AICopilot')
);

const AICopilotTrigger = lazyLoad(
  () => import('@/components/ai-copilot/AICopilotTrigger')
);

interface AppLayoutProps {
  children: React.ReactNode;
}

// Use memo to prevent unnecessary re-renders of the entire layout
const AppLayout: React.FC<AppLayoutProps> = memo(({ children }) => {
  const isMobile = useIsMobile();
  const { isOpen: isAICopilotOpen } = useAICopilot();
  const location = useLocation();
  const path = location.pathname;
  const isDealRoom = path === '/deal-room'; // Add special handling for deal room
  
  // Enhanced logging for debugging
  useEffect(() => {
    console.log("AppLayout mounting with path:", path);
    console.log("Mobile:", isMobile, "AI Copilot Open:", isAICopilotOpen);
    console.log("Is Deal Room:", isDealRoom);
    console.log("Children present:", !!children);
    
    // Add a debug element to page
    const debugElement = document.createElement('div');
    debugElement.id = 'debug-app-layout';
    debugElement.style.display = 'none';
    debugElement.dataset.mounted = 'true';
    debugElement.dataset.timestamp = Date.now().toString();
    document.body.appendChild(debugElement);
    
    return () => {
      console.log("AppLayout unmounting");
      if (document.getElementById('debug-app-layout')) {
        document.getElementById('debug-app-layout')!.remove();
      }
    };
  }, []); // Only run on initial mount, not on re-renders or path changes

  return (
    <>
      <div 
        className="flex h-screen w-full overflow-hidden bg-gradient-to-br from-background to-muted/30"
        data-version={`render-${Date.now()}`}
        data-build-timestamp={window._buildTimestamp || 'unknown'}
        data-render-timestamp={Date.now()}
        data-path={path}
        data-is-deal-room={isDealRoom ? 'true' : 'false'}
        id="app-layout-root"
      >
        {/* Only show AICopilot container on desktop */}
        {!isMobile && (
          <div className="w-[400px] shrink-0 z-40 border-r border-muted/20">
            <LazyLoadWrapper>
              <AICopilot />
            </LazyLoadWrapper>
          </div>
        )}
          
        {/* Only show Sidebar when not on mobile */}
        {!isMobile && (
          <div className="w-[220px] shrink-0 border-r border-muted/20">
            <Sidebar isMobile={isMobile} />
          </div>
        )}
        
        {/* Main content area - flex-grow to take remaining space */}
        {/* Special class for deal-room to ensure proper expansion */}
        <div className={`flex-grow min-w-0 overflow-auto ${isDealRoom ? 'deal-room-layout' : ''}`} id="main-content-area">
          {children}
          
          {/* Memory usage indicator lazy-loaded with fallback */}
          {!isMobile && process.env.NODE_ENV !== 'production' && (
            <div className="fixed bottom-2 right-2 z-10">
              <Suspense fallback={<div className="w-6 h-6" />}>
                <MemoryUsageIndicator />
              </Suspense>
            </div>
          )}
        </div>
      </div>

      {/* Mobile-specific components */}
      {isMobile && <MobileNav />}
      {isMobile && (
        <LazyLoadWrapper>
          <AICopilotTrigger />
        </LazyLoadWrapper>
      )}

      {/* Mobile AI Copilot - render directly without layout flow control */}
      {isMobile && isAICopilotOpen && (
        <div className="fixed inset-0 z-50">
          <AICopilot />
        </div>
      )}

      {/* Deployment tracking */}
      <LayoutDeploymentTracker />
    </>
  );
});

// Add display name for debugging
AppLayout.displayName = 'AppLayout';

export default AppLayout;
